/**
 * Product catalog hooks — storefront only.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Product, PaginatedResponse, Category, Tag } from '@/types'

export interface ProductFilters {
  q?: string
  /** Comma-separated category slugs — products in any category (incl. subcategories) */
  categories?: string
  /** @deprecated use `categories` — single slug kept for legacy shortcodes */
  category?: string
  tag?: string
  /** Comma-separated tag slugs — products matching any tag */
  tags?: string
  /** Comma-separated product slugs — curated list in selection order */
  products?: string
  min_price?: number
  max_price?: number
  min_rating?: number
  in_stock?: boolean
  sort?: 'relevance' | 'price_asc' | 'price_desc' | 'rating' | 'newest'
  page?: number
  per_page?: number
  autocomplete?: boolean
}

// ─── Product listing ──────────────────────────────────────────────────────────

const EMPTY_PRODUCTS_PAGE: PaginatedResponse<Product> = {
  data: [],
  meta: { current_page: 1, last_page: 1, per_page: 24, total: 0, from: 0, to: 0 },
  links: { first: '', last: '', prev: null, next: null },
}

/** Laravel paginator JSON (flat) vs our PaginatedResponse shape (nested meta). */
type LaravelProductPage = {
  data?: Product[]
  meta?: PaginatedResponse<Product>['meta']
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
  from?: number | null
  to?: number | null
}

function normalizeProductPage(raw: LaravelProductPage | null | undefined): PaginatedResponse<Product> {
  if (!raw) return EMPTY_PRODUCTS_PAGE

  if (raw.meta) {
    return {
      data: raw.data ?? [],
      meta: raw.meta,
      links: { first: '', last: '', prev: null, next: null },
    }
  }

  return {
    data: raw.data ?? [],
    meta: {
      current_page: raw.current_page ?? 1,
      last_page: raw.last_page ?? 1,
      per_page: raw.per_page ?? 24,
      total: raw.total ?? 0,
      from: raw.from ?? 0,
      to: raw.to ?? 0,
    },
    links: { first: '', last: '', prev: null, next: null },
  }
}

export type ProductListFilters = Omit<ProductFilters, 'page'>

export function useProducts(filters: ProductFilters = {}) {
  return useQuery({
    queryKey: ['products', filters],
    queryFn: async () => {
      const { data } = await api.get<LaravelProductPage>('/products', {
        params: filters,
      })
      return normalizeProductPage(data)
    },
    staleTime: 1000 * 60 * 5,
  })
}

/** Paginated product listing with infinite scroll (append pages on load more). */
export function useInfiniteProducts(
  filters: ProductListFilters = {},
  options?: { enabled?: boolean },
) {
  const perPage = filters.per_page ?? 24

  return useInfiniteQuery({
    queryKey: ['products', 'infinite', filters],
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<LaravelProductPage>('/products', {
        params: { ...filters, page: pageParam, per_page: perPage },
      })
      return normalizeProductPage(data)
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { meta } = lastPage
      if (!meta || meta.current_page >= meta.last_page) return undefined
      return meta.current_page + 1
    },
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 5,
  })
}

// ─── Single product ───────────────────────────────────────────────────────────

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['products', slug],
    queryFn: async () => {
      const { data } = await api.get<{ data: Product }>(`/products/${slug}`)
      return data.data
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  })
}

// ─── Autocomplete ─────────────────────────────────────────────────────────────

export function useAutocomplete(query: string) {
  return useQuery({
    queryKey: ['products', 'autocomplete', query],
    queryFn: async () => {
      const { data } = await api.get<{ data: Product[] }>('/products', {
        params: { autocomplete: 1, q: query },
      })
      return data.data.slice(0, 8)
    },
    enabled: query.length >= 2,
    staleTime: 1000 * 60 * 2,
  })
}

// ─── Categories ───────────────────────────────────────────────────────────────

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await api.get<Category[] | { data: Category[] }>('/categories')
      return Array.isArray(data) ? data : (data.data ?? [])
    },
    staleTime: 1000 * 60 * 10,
  })
}

export function useTags() {
  return useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const { data } = await api.get<Tag[] | { data: Tag[] }>('/tags')
      return Array.isArray(data) ? data : (data.data ?? [])
    },
    staleTime: 1000 * 60 * 10,
  })
}

export function useCategoryProducts(slug: string, filters: ProductFilters = {}) {
  return useQuery({
    queryKey: ['categories', slug, 'products', filters],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Product>>(
        `/categories/${slug}/products`,
        { params: filters },
      )
      return data
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  })
}

// ─── Product lookup by barcode ────────────────────────────────────────────────

export function useProductLookup(code: string) {
  return useQuery({
    queryKey: ['products', 'lookup', code],
    queryFn: async () => {
      const { data } = await api.get<{ data: Product }>('/products/lookup', {
        params: { code },
      })
      return data.data
    },
    enabled: !!code,
    retry: false,
  })
}
